/* Offline queue for public kiosk form submissions.
   Storage: localStorage under "expoflo:lead-queue" — array of
   { id, formSlug, payload, ts }.
   The queue is flushed whenever the browser is online (on load and on
   the "online" event). Each entry carries a stable `client_submission_id`
   so the backend can dedupe if the server accepted the payload but the
   client never received the response. */

import { api } from "@/lib/api";

const KEY = "expoflo:lead-queue";

function readQueue() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeQueue(items) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* storage full — ignore */
  }
}

function makeId() {
  // Simple client id — not cryptographic, only needs to be unique per browser
  return `csid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function queueSize() {
  return readQueue().length;
}

export function queueSubmission(formSlug, payload) {
  const items = readQueue();
  const entry = {
    id: makeId(),
    formSlug,
    payload: {
      ...payload,
      client_submission_id: payload.client_submission_id || makeId(),
    },
    ts: Date.now(),
  };
  items.push(entry);
  writeQueue(items);
  return entry;
}

/* Try to POST every pending submission. Returns { attempted, sent, remaining }.
   Runs in-order and stops early on the first network failure (so the next
   attempt does not create out-of-order duplicates when the network flaps). */
export async function flushQueue() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { attempted: 0, sent: 0, remaining: readQueue().length };
  }
  const items = readQueue();
  if (items.length === 0) return { attempted: 0, sent: 0, remaining: 0 };

  let sent = 0;
  let attempted = 0;
  const remaining = [];

  for (let i = 0; i < items.length; i += 1) {
    const it = items[i];
    attempted += 1;
    try {
      await api.post(`/public/lead-form/${it.formSlug}/submit`, it.payload);
      sent += 1;
      // success — drop this one, keep going
    } catch (err) {
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        // Server rejected the payload permanently (e.g. form deleted / validation).
        // Drop it so it does not block the rest of the queue forever.
        continue;
      }
      // Network / 5xx — keep this and the remainder, stop attempting for now
      remaining.push(...items.slice(i));
      break;
    }
  }

  writeQueue(remaining);
  return { attempted, sent, remaining: remaining.length };
}

/* Wire browser online/offline signals to callbacks. Returns an unsubscribe. */
export function subscribeConnectivity({ onChange, autoFlush = true } = {}) {
  function update() {
    const online = typeof navigator === "undefined" ? true : navigator.onLine;
    if (onChange) onChange({ online, pending: queueSize() });
    if (autoFlush && online) {
      flushQueue().then((r) => {
        if (onChange) onChange({ online, pending: r.remaining });
      });
    }
  }
  update();
  window.addEventListener("online", update);
  window.addEventListener("offline", update);
  // Also poll periodically to pick up any late writes (e.g. from other tabs)
  const iv = setInterval(update, 15000);
  return () => {
    window.removeEventListener("online", update);
    window.removeEventListener("offline", update);
    clearInterval(iv);
  };
}
